// Satellite nubi MTG v2: frame EUMETSAT con TIME esplicito, GeoColour di giorno
// / IR 10.5µm di notte (elevazione solare approssimata sulla Sicilia, soglia
// +5°), doppio buffer e timeline nowcast condivisa. L'ultimo istante osservato
// arriva esclusivamente dal contratto live /status: il clock del browser non
// viene mai trasformato in una falsa disponibilita' del satellite.
import { NowcastTimeline } from '../ui/nowcast-timeline.js';
import { OBSERVATION_AREAS } from '../lib/observation-areas.js';
import { enforceObservationLayerOrder } from '../lib/observation-layer-order.js';

const SATELLITE_API = 'https://api.weathersicily.it/v1/satellite';
const LAYER_DAY = 'mtg_fd:rgb_geocolour';
const LAYER_NIGHT = 'mtg_fd:ir105_hrfi';
const STEP_MIN = 10;
const OPACITY = 0.72;
const STATUS_REFRESH_MS = 5 * 60000;
const STATUS_RETRY_MS = 60000;
const STATUS_TIMEOUT_MS = 12000;

const state = {
    area: 'sicily',
    worldSources: [],
    worldTimes: [],
    product: 'auto',
    map: null,
    enabled: false,
    currentKey: '',
    active: null,
    pending: null,
    gen: 0,
    registered: false,
    latestAvailableMs: NaN,
    displayedFrameMs: NaN,
    cadenceMinutes: NaN,
    source: '',
    sourceMode: '',
    feedStatus: 'loading',
    feedAgeMinutes: NaN,
    feedMaximumAgeMinutes: NaN,
    statusFetchedAt: 0,
    statusTimer: null,
    statusAbort: null,
    statusRequestId: 0,
    statusBadge: null,
};

const HHMM = new Intl.DateTimeFormat('it-IT', {
    hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Rome',
});

function isoFromDate(date, stepMinutes = STEP_MIN) {
    const d = new Date(date.getTime());
    d.setSeconds(0, 0);
    d.setMinutes(d.getMinutes() - (d.getMinutes() % stepMinutes), 0, 0);
    return d.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

// Elevazione solare approssimata (37.5N 14.2E), soglia +5° (port v1).
function isDaylight(date) {
    const center = state.map?.getCenter();
    const lat = (center?.lat ?? 37.5) * Math.PI / 180;
    const lonHours = (center?.lng ?? 14.2) / 15;
    const start = Date.UTC(date.getUTCFullYear(), 0, 0);
    const doy = Math.floor((date.getTime() - start) / 86400000);
    const decl = 23.45 * Math.PI / 180 * Math.sin(2 * Math.PI * (284 + doy) / 365);
    const solarHour = date.getUTCHours() + date.getUTCMinutes() / 60 + lonHours;
    const hourAngle = (solarHour - 12) * 15 * Math.PI / 180;
    const elev = Math.asin(Math.sin(lat) * Math.sin(decl) + Math.cos(lat) * Math.cos(decl) * Math.cos(hourAngle));
    return elev > (5 * Math.PI / 180);
}

function satelliteTiles(layer, iso) {
    // Gli alias mtg_fd:* restano il percorso live. Quando /status dichiara il
    // live indisponibile ma l'archivio locale valido, qui arriva invece il nome
    // canonico del COG e lo stesso TIME esatto, mai un latest rietichettato.
    return `${SATELLITE_API}/tile?layer=${encodeURIComponent(layer)}`
        + `&time=${encodeURIComponent(iso)}&size=256&bbox={bbox-epsg-3857}`;
}

function conciseSource(source) {
    if (/archivio/i.test(source || '')) return 'EUMETSAT · archivio';
    return /eumetsat/i.test(source || '') ? 'EUMETSAT' : (source || 'Fonte satellite');
}

function ensureStatusBadge() {
    if (!state.statusBadge) {
        state.statusBadge = document.createElement('span');
        state.statusBadge.className = 'wsv2-satellite-truth';
        state.statusBadge.setAttribute('role', 'status');
        state.statusBadge.setAttribute('aria-live', 'polite');
    }
    const dock = document.querySelector('.wsv2-nowcast-timeline');
    if (dock) {
        const liveButton = dock.querySelector('.wsv2-nt-live');
        if (state.statusBadge.parentElement !== dock) {
            if (liveButton) dock.insertBefore(state.statusBadge, liveButton);
            else dock.appendChild(state.statusBadge);
        }
        state.statusBadge.classList.remove('is-standalone');
    } else {
        if (state.statusBadge.parentElement !== document.body) document.body.appendChild(state.statusBadge);
        state.statusBadge.classList.add('is-standalone');
    }
    state.statusBadge.hidden = !state.enabled;
    return state.statusBadge;
}

function updateStatusBadge(errorMessage = '') {
    if (!state.enabled) return;
    const badge = ensureStatusBadge();
    const hasFrame = Number.isFinite(state.displayedFrameMs) || Number.isFinite(state.latestAvailableMs);
    const frameMs = Number.isFinite(state.displayedFrameMs) ? state.displayedFrameMs : state.latestAvailableMs;
    const stale = state.feedStatus !== 'ok'
        || (Number.isFinite(state.feedAgeMinutes) && Number.isFinite(state.feedMaximumAgeMinutes)
            && state.feedAgeMinutes > state.feedMaximumAgeMinutes);
    badge.classList.toggle('is-warning', !!errorMessage || (hasFrame && stale));
    badge.classList.toggle('is-error', !hasFrame && !!errorMessage);
    if (!hasFrame) {
        delete badge.dataset.source;
        delete badge.dataset.validAt;
        delete badge.dataset.latestAvailableAt;
        delete badge.dataset.cadenceMinutes;
        badge.replaceChildren();
        const b = document.createElement('b');
        b.textContent = errorMessage ? '🛰️ Satellite non disponibile' : '🛰️ Verifica satellite…';
        badge.appendChild(b);
        badge.title = errorMessage || 'Lettura dell’ultimo frame reale dal gateway Weather Sicily.';
        return;
    }
    const source = conciseSource(state.source);
    const b = document.createElement('b');
    const small = document.createElement('small');
    b.textContent = `🛰️ ${source}${stale || errorMessage ? ' · ritardo' : ''}`;
    const worldFrameTimes = state.area === 'world' ? state.worldSources.map(source => [...source.times].reverse().find(t => t*1000 <= frameMs)).filter(Number.isFinite) : [];
    small.textContent = worldFrameTimes.length
        ? `frame ${HHMM.format(new Date(Math.min(...worldFrameTimes)*1000))}–${HHMM.format(new Date(Math.max(...worldFrameTimes)*1000))} · orari delle fonti`
        : `frame ${HHMM.format(new Date(frameMs))} · ogni ${state.cadenceMinutes}′`;
    badge.replaceChildren(b, small);
    const displayedIso = new Date(frameMs).toISOString();
    const latestIso = new Date(state.latestAvailableMs).toISOString();
    badge.dataset.source = state.source;
    badge.dataset.validAt = displayedIso;
    badge.dataset.latestAvailableAt = latestIso;
    badge.dataset.cadenceMinutes = String(state.cadenceMinutes);
    badge.title = [
        `Fonte: ${state.source}.`,
        `Frame visualizzato: ${displayedIso}.`,
        `Ultimo disponibile: ${latestIso}.`,
        `Cadenza dichiarata: ${state.cadenceMinutes} minuti.`,
        errorMessage || '',
    ].filter(Boolean).join(' ');
}

function ids(n) { return { src: `ws-sat-src-${n}`, lyr: `ws-sat-${n}` }; }

function removeBuffer(buf) {
    const map = state.map;
    if (!buf) return;
    buf.cancel?.();
    if (buf.members) { buf.members.forEach(removeBuffer); return; }
    try { if (map.getLayer(buf.lyr)) map.removeLayer(buf.lyr); } catch (e) {}
    try { if (map.getSource(buf.src)) map.removeSource(buf.src); } catch (e) {}
}

function showTime(iso) {
    const map = state.map;
    if (!state.enabled) return;
    if (iso === state.currentKey && state.active) return;
    if (state.area === 'world') { showWorldTime(iso); return; }
    removeBuffer(state.pending);
    state.pending = null;

    // GeoColour is day/night blended across the disc: Europe must not use
    // Sicily's day/night decision for locations in a different solar regime.
    const isDay = state.product === 'visible' || (state.product === 'auto'
        && (state.area === 'europe' || isDaylight(new Date(iso))));
    const layer = state.sourceMode === 'archive'
        ? (isDay ? 'natural_color' : 'colorized_ir_clouds')
        : (isDay ? LAYER_DAY : LAYER_NIGHT);
    const gen = ++state.gen;
    const buf = ids(gen);
    map.addSource(buf.src, { type: 'raster', tiles: [satelliteTiles(layer, iso)], tileSize: 256,
        bounds: OBSERVATION_AREAS[state.area].bounds,
        attribution: 'Satellite © EUMETSAT',
    });
    const radarLayer = map.getStyle().layers.find(item => /^ws-radar-\d+$/.test(item.id) || item.id === 'ws-blitz');
    const before = radarLayer?.id || (map.getLayer('ws-stations-symbols') ? 'ws-stations-symbols' : undefined);
    // PRIMA accensione: niente doppio buffer, tile visibili man mano che
    // arrivano (percezione immediata — EUMETSAT può metterci secondi a tile).
    // Il doppio buffer resta per i CAMBI di orario (evita i quadrettoni).
    const firstShow = !state.active;
    map.addLayer({
        id: buf.lyr, type: 'raster', source: buf.src,
        paint: { 'raster-opacity': firstShow ? OPACITY : 0, 'raster-fade-duration': 120 },
    }, before);
    enforceObservationLayerOrder(map);
    state.pending = buf;
    if (firstShow) {
        state.active = buf;
        state.pending = null;
        state.currentKey = iso;
        return;
    }

    // Doppio buffer: svelo il frame quando i suoi tile sono caricati, con
    // SBLOCCO di sicurezza a 6s (una tile lenta non deve congelare lo scrub).
    let revealed = false;
    const reveal = () => {
        if (revealed) return;
        revealed = true;
        map.off('sourcedata', onData);
        if (state.pending !== buf || gen !== state.gen) { removeBuffer(buf); return; }
        map.setPaintProperty(buf.lyr, 'raster-opacity', OPACITY);
        removeBuffer(state.active);
        state.active = buf;
        state.pending = null;
        state.currentKey = iso;
    };
    const onData = (e) => {
        if (e.sourceId !== buf.src || !e.isSourceLoaded) return;
        reveal();
    };
    map.on('sourcedata', onData);
    setTimeout(reveal, 6000);
}

function showWorldTime(iso) {
    const map = state.map;
    removeBuffer(state.pending);
    state.pending = null;
    const generation = ++state.gen;
    const firstShow = !state.active;
    const buffer = { members: [] };
    const before = map.getStyle().layers.find(layer => /^ws-radar-/.test(layer.id) || layer.id === 'ws-blitz')?.id
        || (map.getLayer('ws-stations-symbols') ? 'ws-stations-symbols' : undefined);
    const acquisitions = state.worldSources.map(source => [...source.times].reverse().find(t => t*1000 <= Date.parse(iso))).join(',');
    const mosaic = [{tiles:`${SATELLITE_API}/world-tile?style=ir-v4&time={time}&frames=${acquisitions}&z={z}&x={x}&y={y}`,
        times:state.worldTimes.map(t => Date.parse(t)/1000),maxzoom:6,bounds:[-180,-80,180,80],
        attribution:'Satellite © EUMETSAT / NOAA / JMA · NASA GIBS'}];
    for (const [index, source] of mosaic.entries()) {
        const frameTime = [...source.times].reverse().find(t => t*1000 <= Date.parse(iso));
        if (!Number.isFinite(frameTime)) continue;
        const exactIso = new Date(frameTime*1000).toISOString().replace('.000Z','Z');
        const member = {src: `ws-sat-src-${generation}-${index}`, lyr: `ws-sat-${generation}-${index}`};
        map.addSource(member.src, {type:'raster', tiles:[source.tiles.replace('{time}', encodeURIComponent(exactIso))],
            tileSize:256, maxzoom:source.maxzoom, bounds:source.bounds, attribution:source.attribution});
        map.addLayer({id:member.lyr, type:'raster', source:member.src,
            paint:{'raster-opacity':firstShow ? OPACITY : 0, 'raster-fade-duration':0}}, before);
        buffer.members.push(member);
    }
    enforceObservationLayerOrder(map);
    if (firstShow) { state.active = buffer; state.currentKey = iso; return; }
    state.pending = buffer;
    const reveal = () => {
        buffer.cancel();
        if (!state.enabled || state.pending !== buffer || generation !== state.gen) return;
        for (const member of buffer.members) map.setPaintProperty(member.lyr, 'raster-opacity', OPACITY);
        removeBuffer(state.active);
        state.active = buffer;
        state.pending = null;
        state.currentKey = iso;
    };
    const onData = () => {
        if (buffer.members.every(member => map.isSourceLoaded(member.src))) reveal();
    };
    const timer = setTimeout(reveal, 6000);
    buffer.cancel = () => { clearTimeout(timer); map.off('sourcedata', onData); };
    map.on('sourcedata', onData);
}

function showAtTime(T) {
    // Fail closed: senza un frame dichiarato dal feed live non si costruisce
    // alcun URL. Il cursore puo' tornare nel passato, mai oltre latest_valid_at.
    if (!Number.isFinite(state.latestAvailableMs)) return;
    const requested = Number.isFinite(Number(T)) ? Number(T) : state.latestAvailableMs;
    const stepMinutes = Number.isFinite(state.cadenceMinutes) ? state.cadenceMinutes : STEP_MIN;
    const iso = state.area === 'world'
        ? [...state.worldTimes].reverse().find(time => Date.parse(time) <= Math.min(requested, state.latestAvailableMs))
        : isoFromDate(new Date(Math.min(requested, state.latestAvailableMs)), stepMinutes);
    if (!iso) {
        removeBuffer(state.pending); removeBuffer(state.active);
        state.pending = state.active = null; state.currentKey = '';
        updateStatusBadge('Nessun frame disponibile per questo orario.');
        return;
    }
    state.displayedFrameMs = Date.parse(iso);
    updateStatusBadge();
    showTime(iso);
}

function scheduleStatusRefresh(delayMs) {
    if (state.statusTimer) clearTimeout(state.statusTimer);
    state.statusTimer = state.enabled
        ? setTimeout(() => refreshSatelliteStatus(), delayMs)
        : null;
}

async function refreshSatelliteStatus() {
    if (!state.enabled) return;
    if (state.statusAbort) state.statusAbort.abort();
    const requestId = ++state.statusRequestId;
    const controller = new AbortController();
    state.statusAbort = controller;
    const timeout = setTimeout(() => controller.abort(), state.area === 'world' ? 25000 : STATUS_TIMEOUT_MS);
    let success = false;
    try {
        const response = await fetch(`${SATELLITE_API}/status${state.area === 'world' ? '?area=world' : ''}`, {
            cache: 'no-store',
            headers: { Accept: 'application/json' },
            signal: controller.signal,
        });
        const payload = await response.json();
        if (!response.ok || payload?.success !== true) throw new Error(`status HTTP ${response.status}`);
        const data = payload?.data || {};
        const live = data.live_feed || {};
        const archive = data.local_archive || {};
        const liveLatestMs = Date.parse(live.latest_valid_at || '');
        const archiveLatestMs = Date.parse(archive.latest_valid_at || '');
        const useLive = (live.status === 'ok' || (state.area === 'world' && live.status === 'degraded')) && Number.isFinite(liveLatestMs);
        const useArchive = !useLive && archive.status === 'ok' && Number.isFinite(archiveLatestMs);
        if (!useLive && !useArchive) throw new Error('nessun frame satellite certificato disponibile');
        const latestMs = useLive ? liveLatestMs : archiveLatestMs;
        const cadence = useLive ? Number(live.cadence_minutes) : 60;
        const source = useLive
            ? String(live.source || '').trim()
            : 'EUMETSAT Data Store · archivio locale Weather Sicily';
        if (!Number.isFinite(latestMs)) throw new Error('latest_valid_at assente o non valido');
        if (!Number.isFinite(cadence) || cadence <= 0 || cadence > 60) throw new Error('cadenza non valida');
        if (!source) throw new Error('sorgente non dichiarata');
        // Una data molto futura indica un contratto corrotto. Questo controllo
        // protegge soltanto la plausibilita': non calcola mai l'ultimo frame.
        const serverGeneratedMs = Number(payload?.meta?.generated_at) * 1000;
        if (Number.isFinite(serverGeneratedMs) && latestMs > serverGeneratedMs + cadence * 60000) {
            throw new Error('latest_valid_at successivo al clock del gateway');
        }
        if (!state.enabled || requestId !== state.statusRequestId) return;

        let worldChanged = false;
        if (state.area === 'world') {
            worldChanged = JSON.stringify(state.worldSources) !== JSON.stringify(data.sources);
            if (worldChanged) state.currentKey = '';
            if (!Array.isArray(data.sources) || !data.sources.length || !Array.isArray(data.available_times)) throw new Error('mosaico mondiale assente');
            state.worldSources = data.sources;
            state.worldTimes = data.available_times;
        }
        const sourceMode = state.area === 'world' ? 'world' : useLive ? 'live' : 'archive';
        const changed = latestMs !== state.latestAvailableMs || sourceMode !== state.sourceMode;
        state.latestAvailableMs = latestMs;
        state.cadenceMinutes = cadence;
        state.source = source;
        state.sourceMode = sourceMode;
        state.feedStatus = useLive ? String(live.status) : 'archive';
        state.feedAgeMinutes = Number(useLive ? live.age_minutes : archive.age_minutes);
        state.feedMaximumAgeMinutes = Number(useLive ? live.maximum_age_minutes : archive.maximum_age_minutes);
        state.statusFetchedAt = Date.now();

        if (!state.registered) {
            // Clock prima della register: buildGrid nasce gia' sul frame reale,
            // senza una singola renderizzazione basata sull'orologio client.
            NowcastTimeline.setDataClock('satellite', latestMs);
            NowcastTimeline.register('satellite', showAtTime);
            state.registered = true;
            ensureStatusBadge();
        } else if (changed) {
            NowcastTimeline.setDataClock('satellite', latestMs);
        } else if (worldChanged) {
            NowcastTimeline.notifyDataUpdated();
        }
        updateStatusBadge();
        success = true;
    } catch (error) {
        if (!state.enabled || requestId !== state.statusRequestId) return;
        const reason = error?.name === 'AbortError'
            ? 'Timeout durante la verifica del feed live.'
            : `Verifica feed non riuscita: ${error?.message || 'errore sconosciuto'}.`;
        // Se esiste gia' un frame esatto lo si conserva dichiarandone il
        // ritardo; al primo errore non si monta nessun tile e non si inventa T.
        updateStatusBadge(reason);
    } finally {
        clearTimeout(timeout);
        if (state.statusAbort === controller) state.statusAbort = null;
        if (state.enabled && requestId === state.statusRequestId) {
            scheduleStatusRefresh(success ? STATUS_REFRESH_MS : STATUS_RETRY_MS);
        }
    }
}

export function initSatellite(map) {
    state.map = map;
    document.addEventListener('visibilitychange', () => {
        if (!state.enabled || document.visibilityState !== 'visible') return;
        if (Date.now() - state.statusFetchedAt >= STATUS_REFRESH_MS) refreshSatelliteStatus();
    });
}

export function setSatelliteOptions({ area = state.area, product = state.product } = {}) {
    if (!OBSERVATION_AREAS[area] || !['auto', 'visible', 'ir'].includes(product)) return;
    if (state.area === area && state.product === product) return;
    const restart = state.enabled && ((state.area === 'world') !== (area === 'world'));
    if (restart) setSatelliteEnabled(false);
    state.area = area;
    state.product = product;
    if (restart) { setSatelliteEnabled(true); return; }
    state.currentKey = '';
    if (state.enabled && Number.isFinite(state.displayedFrameMs)) {
        showTime(isoFromDate(new Date(state.displayedFrameMs), state.cadenceMinutes));
    }
}

export function setSatelliteEnabled(on) {
    if (on === state.enabled) return;
    state.enabled = on;
    if (on) {
        state.feedStatus = 'loading';
        ensureStatusBadge();
        updateStatusBadge();
        refreshSatelliteStatus();
    } else {
        if (state.statusTimer) clearTimeout(state.statusTimer);
        state.statusTimer = null;
        if (state.statusAbort) state.statusAbort.abort();
        state.statusAbort = null;
        state.statusRequestId++;
        if (state.registered) NowcastTimeline.unregister('satellite');
        state.registered = false;
        NowcastTimeline.clearDataClock('satellite');
        removeBuffer(state.pending);
        removeBuffer(state.active);
        state.pending = state.active = null;
        state.currentKey = '';
        state.latestAvailableMs = NaN;
        state.displayedFrameMs = NaN;
        state.cadenceMinutes = NaN;
        state.source = '';
        state.sourceMode = '';
        state.worldSources = [];
        state.worldTimes = [];
        state.gen++;
        state.statusFetchedAt = 0;
        if (state.statusBadge) {
            state.statusBadge.remove();
            state.statusBadge = null;
        }
    }
}
